import { shareAsync } from './share-async';
import { readWikiToken } from './wiki-auth';

export type WikiRole = '' | 'admin' | 'moderator' | 'super_admin';

export type WikiPermission = {
  role: WikiRole;
  canManageWiki: boolean;
  canEditProfile: boolean;
  canManageAdmins: boolean;
  canManagePermissions: boolean;
};

export type PermissionUser = {
  uid: string;
  username: string;
  avatar?: string;
  gameKey?: string;
  role?: WikiRole;
  builtIn?: boolean;
};

export type PermissionAssignments = {
  superAdmins: PermissionUser[];
  builtInSuperAdmins: PermissionUser[];
  moderators: PermissionUser[];
};

const myPermissionInflight = new Map<string, Promise<WikiPermission>>();
const EMPTY_PERMISSION: WikiPermission = {
  role: '',
  canManageWiki: false,
  canEditProfile: false,
  canManageAdmins: false,
  canManagePermissions: false,
};

async function permissionRequest<T>(path: string, init?: RequestInit) {
  const token = readWikiToken();
  if (!token) throw new Error('登录状态已失效，请重新登录');
  const response = await fetch(path, {
    ...init,
    cache: 'no-store',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      'access-token': token,
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || '权限操作失败');
  return payload;
}

export function fetchMyWikiPermission(game = 'platform') {
  // 只按 game 去重：myInfo 刷新可能换 token，不能把 token 编进 key
  return shareAsync(
    myPermissionInflight,
    game,
    () =>
      permissionRequest<WikiPermission>(
        `/api/permissions/me?game=${encodeURIComponent(game)}`,
      ),
    4000,
  );
}

export { EMPTY_PERMISSION };

export function fetchPermissionAssignments() {
  return permissionRequest<PermissionAssignments>('/api/permissions');
}

export async function lookupPermissionUser(uid: string) {
  const payload = await permissionRequest<{ user: PermissionUser }>(
    `/api/permissions/lookup?uid=${encodeURIComponent(uid)}`,
  );
  return payload.user;
}

export async function addPermissionAssignment(input: {
  uid: string;
  role: 'super_admin' | 'moderator';
  gameKey?: string;
}) {
  await permissionRequest('/api/permissions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function removePermissionAssignment(input: {
  uid: string;
  role: 'super_admin' | 'moderator';
  gameKey?: string;
}) {
  const query = new URLSearchParams({ uid: input.uid, role: input.role });
  if (input.gameKey) query.set('game', input.gameKey);
  await permissionRequest(`/api/permissions?${query}`, { method: 'DELETE' });
}
